import { forwardRef, useMemo } from 'react';
import { DayPicker, DayPickerProps } from 'react-day-picker';

import { ReckonThemeWithPalette, useTheme } from '@reckon-web/theme';
import { getContrastText, hexToRgb } from '@reckon-web/utils';

export type CalendarProps = DayPickerProps;

export const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  ({ modifiers, components, ...props }, ref) => {
    const theme = useTheme();
    const styles = useMemo(() => getCalendarStyles(theme), [theme]);
    const indexOfMonday = 1;
    const augmentedModifiers = useMemo(
      () => ({
        ...modifiers,
        weekend: (date: Date) => [0, 6].includes(date.getDay()),
      }),
      [modifiers]
    );

    const customComponents = {
      Chevron: ({
        orientation,
        ...props
      }: {
        orientation?: 'left' | 'right' | 'up' | 'down';
      }) => {
        const paths = {
          left: '15 18 9 12 15 6',
          right: '9 18 15 12 9 6',
          up: '18 15 12 9 6 15',
          down: '6 9 12 15 18 9',
        };

        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            stroke={theme.palette.listItem.text}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            {...props}
          >
            <polyline points={paths[orientation || 'right']} />
          </svg>
        );
      },
      ...components,
    };

    return (
      <div css={styles} ref={ref}>
        <DayPicker
          weekStartsOn={indexOfMonday}
          modifiers={augmentedModifiers}
          components={customComponents}
          {...props}
        />
      </div>
    );
  }
);

// Updated Styles for v9
// ------------------------------

function getCalendarStyles(theme: ReckonThemeWithPalette) {
  const cellSize = theme.sizing.base;
  const navButtonSize = theme.sizing.xsmall;
  const interactionColor = theme.palette.actions.active;
  const rangeBetweenColor = hexToRgb(interactionColor, 0.2);

  return {
    color: theme.palette.text.base,
    padding: theme.spacing.small,
    // resets and wrapper stuff
    '.rdp-root': {
      display: 'inline-block',
      fontSize: '1rem',
      position: 'relative',
      flexDirection: 'row',
      userSelect: 'none',
      outline: 0,
    },
    '.rdp-months': {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    '.rdp-month': {
      display: 'table',
      // separate weeks for easier parsing of range selection
      borderSpacing: '0 2px',
      borderCollapse: 'separate',
      // separate months for easier parsing of range selection
      margin: theme.spacing.small,
      // NOTE: resolve weird safari bug:
      // https://bugs.webkit.org/show_bug.cgi?id=187903
      position: 'relative',
      '.rdp-month_caption > div': { position: 'absolute' },
    },
    // the caption is the day/month title e.g. "July 2020"
    '.rdp-month_caption': {
      display: 'table-caption',
      height: navButtonSize,
      marginBottom: '0.5em',
      padding: '0 0.5em',
      textAlign: 'left',
    },
    '.rdp-caption_label': {
      fontWeight: theme.typography.fontWeight.medium,
      fontSize: theme.typography.fontSize.medium,
    },
    // weekdays
    '.rdp-weekdays': { display: 'table-row', marginTop: '1em' },
    '.rdp-weekday': {
      color: theme.palette.text.dim,
      display: 'table-cell',
      fontSize: theme.typography.fontSize.small,
      fontWeight: theme.typography.fontWeight.medium,
      padding: '0.5em',
      textAlign: 'center',
    },
    '.rdp-weeks': {
      display: 'table-row-group',
      fontSize: theme.typography.fontSize.small,
      fontWeight: theme.typography.fontWeight.medium,
    },
    '.rdp-week': { display: 'table-row' },
    // nav buttons
    '.rdp-nav': {
      display: 'flex',
      position: 'absolute',
      right: theme.spacing.xsmall,
      top: theme.spacing.xsmall,
      zIndex: 1,
    },
    '.rdp-nav button': {
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      borderRadius: theme.radii.xsmall,
      color: theme.palette.listItem.text,
      cursor: 'pointer',
      display: 'inline-block',
      height: theme.sizing.small,
      width: theme.sizing.small,
      border: 'none',
      backgroundColor: 'transparent',
      left: 'auto',
      '&:hover, &:focus-visible': {
        backgroundColor: theme.palette.listItem.backgroundFocused,
        color: theme.palette.listItem.textFocused,
        outline: 0,
      },
      '&:active': {
        backgroundColor: theme.palette.listItem.backgroundPressed,
        color: theme.palette.listItem.textPressed,
      },
    },
    '.rdp-day.weekend': {
      color: theme.palette.text.muted,
    },
    '.rdp-day_button:disabled': {
      color: theme.palette.text.dim,
      cursor: 'default',
    },
    // Most reliable: Use data attributes and avoid interfering with class-based selection
    '.rdp-day[role="gridcell"]': {
      borderRadius: '50%',
      display: 'table-cell',
      height: cellSize,
      outline: 0,
      padding: 0,
      position: 'relative',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: cellSize,
      cursor: 'pointer',
    },
    '.rdp-day_button': {
      borderRadius: '50%',
      border: 'none',
      background: 'transparent',
      width: '100%',
      height: '100%',
      cursor: 'inherit',
      position: 'relative',
      zIndex: 2, // Ensure button is above hover effects
    },
    // Use ::after for hover to ensure it's above selected styling
    '.rdp-day[role="gridcell"]:hover:not([aria-disabled="true"])::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '50%',
      boxShadow: `inset 0 0 0 2px ${interactionColor}`,
      pointerEvents: 'none',
      zIndex: 3, // Above everything including selected background
    },
    // Ensure hover border shows on selected days too
    '.rdp-day.rdp-selected[role="gridcell"]:hover:not([aria-disabled="true"])::after':
      {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '50%',
        boxShadow: `inset 0 0 0 2px ${interactionColor}`,
        pointerEvents: 'none',
        zIndex: 4, // Even higher to ensure it shows over selected styling
      },
    // Ensure outside days don't get hover effects and have proper styling
    '.rdp-day.rdp-outside': {
      cursor: 'default',
      color: theme.palette.text.dim,
    },
    '.rdp-day.rdp-today .rdp-day_button': {
      color: theme.palette.text.critical,
      fontWeight: theme.typography.fontWeight.bold,
    },
    '.rdp-day.rdp-selected:not(.rdp-outside)': {
      '& .rdp-day_button': {
        color: getContrastText(interactionColor),
        backgroundColor: interactionColor,
        '&:hover, &:focus-visible': {
          backgroundColor: interactionColor,
        },
      },
    },
    // Range styling that only applies when there's actually a range (start AND end)
    '.rdp-day.rdp-range_start.rdp-range_end:not(.rdp-outside)': {
      // Single day selected - no range background, just circular
      '&::before': {
        display: 'none', // Hide any range background
      },
    },
    '.rdp-day.rdp-range_start:not(.rdp-range_end):not(.rdp-outside)': {
      // Start of a multi-day range
      '&::before': {
        backgroundColor: rangeBetweenColor,
        position: 'absolute',
        content: '" "',
        width: cellSize / 2,
        height: cellSize,
        top: 0,
        right: 0,
        zIndex: -1,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
      },
    },
    '.rdp-day.rdp-range_end:not(.rdp-range_start):not(.rdp-outside)': {
      // End of a multi-day range
      '&::before': {
        backgroundColor: rangeBetweenColor,
        position: 'absolute',
        content: '" "',
        width: cellSize / 2,
        height: cellSize,
        top: 0,
        left: 0,
        zIndex: -1,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
      },
    },
    '.rdp-day.rdp-range_middle.rdp-selected:not(.rdp-outside)': {
      '&, &:hover, &.focus-visible': {
        backgroundColor: rangeBetweenColor,
        borderRadius: 0,
        '& .rdp-day_button': {
          backgroundColor: 'transparent',
          color: theme.palette.text.base,
        },
      },
    },
    // Ensure range start/end have proper selected styling on top
    '.rdp-day.rdp-range_start.rdp-selected:not(.rdp-outside), .rdp-day.rdp-range_end.rdp-selected:not(.rdp-outside)':
      {
        '& .rdp-day_button': {
          color: getContrastText(interactionColor),
          backgroundColor: interactionColor,
          borderRadius: '50%',
          zIndex: 1, // Ensure it appears above the range background
          '&:hover, &:focus-visible': {
            backgroundColor: interactionColor,
          },
        },
      },
    '.rdp-day.rdp-range_middle.firstOfMonth:not(.rdp-outside)': {
      '&, &:hover, &.focus-visible': {
        background: `linear-gradient(to left, ${rangeBetweenColor}, ${theme.palette.background.dialog})`,
      },
    },
    '.rdp-day.rdp-range_middle.lastOfMonth:not(.rdp-outside)': {
      '&, &:hover, &.focus-visible': {
        background: `linear-gradient(to right, ${rangeBetweenColor}, ${theme.palette.background.dialog})`,
      },
    },
  } as const;
}
